import { v4 as uuidv4 } from "uuid";
import { defaultUnitsV2, getPropaneOrLpgUnits } from "../../../../sustell_15/utils/unit-utils";
import { UserProfilePrefs } from "../../../Helpers/UserProfilePrefs";
import {
  floatValueInUserUnits,
  getDefaultTransportUnit,
  getUserTransportUnit,
  setInterventionFloatValue,
} from "../../Baseline/mapper/inCommonMapperSustellV2";
import {
  ShrimpInput as ShrimpInterventionInput,
  GrowingInterventionSupply,
  GrowingInterventionExternalSource,
  ShrimpOperations as ShrimpInterventionOperations,
  FertilizerTypeInput,
  ShrimpMaterialTypeInput,
  ShrimpChemicalTypeInput as ShrimpInterventionChemicalTypeInput,
  ShrimpOutputs as ShrimpInterventionOutputs,
  ShrimpOutput as ShrimpInterventionOutput,
  ShrimpInterventionFeed,
  ShrimpStageData,
  ShrimpEnergyTypeInput as ShrimpInterventionEnergyTypeInput,
  NaturalFeedIntervention,
} from "../../../../sustell_15/models/Intervention/ShrimpIntervention";
import {
  ShrimpOutput as ShrimpBaselineOutput,
  FertilizerTypeInput as ShrimpBaselineFertilizerTypeInput,
  ShrimpMaterialTypeInput as ShrimpBaselineMaterialTypeInput,
  ShrimpChemicalTypeInput as ShrimpBaselineChemicalTypeInput,
  NaturalFeed,
} from "../../../../sustell_15/models/Baseline/ShrimpBaseline";
import {
  createCoreForm,
  isValidValue,
  mapCompoundFeedForOneStage,
  mapResourceUse,
} from "./inCommonMapperSustellV2";
import {
  GrowingInterventionSupply as TempGrowingInterventionSupply,
  GrowingInterventionExternalSource as TempGrowingInterventionExternalSource,
  ShrimpGrowingStageMaterialTypeInput,
  ShrimpOutput,
  ShrimpChemicalTypeInput,
  ShrimpStageDBModel,
  FeedingProgramDBModel,
  ShrimpStageDataDBModel,
  ShrimpInterventionDBModel,
  FertilizerTypeInput as TempFertilizerTypeInput,
  ShrimpEnergyTypeInput as ShrimpEnergyTypeInputDB,
  NaturalFeedDB,
  SingleIngredientUseInterventionDBModel,
} from "../../../../sustell_15/models/TempBackendModels/TempInterventionModels/TempShrimpInterventionDB";
import { EnergyCarrierType } from "../../../../../graphql/generated/blonk/cattle";
import {
  SingleIngredientUseDBModel,
  StageMaterialDBModel,
} from "../../../../sustell_15/models/TempBackendModels/TempShrimpDB";
import { BaselineFeedWithOrigin } from "../../../../sustell_15/models/Baseline/Baseline";
import { InterventionFeed } from "../../../../sustell_15/models/Intervention/Intervention";

const initStageData = (): ShrimpStageData => {
  return {
    input: {
      totalPondArea: "",
      totalPondArea_newValue: "",
      totalPondArea_changeMetric: "",
      emptyPeriod: "",
      emptyPeriod_newValue: "",
      emptyPeriod_changeMetric: "",
      totalWeightAtStartDate: "",
      totalWeightAtStartDate_newValue: "",
      totalWeightAtStartDate_changeMetric: "",
      totalWeightAtEndDate: "",
      totalWeightAtEndDate_newValue: "",
      totalWeightAtEndDate_changeMetric: "",
      priceAtStartDate: "",
      priceAtStartDate_newValue: "",
      priceAtStartDate_changeMetric: "",
      priceAtEndDate: "",
      priceAtEndDate_newValue: "",
      priceAtEndDate_changeMetric: "",
      internalSources: [],
      externalSources: [],
      internalSourcesAdditions: [],
      externalSourcesAdditions: [],
    },
    feed: {
      compoundFeeds: [],
      singleIngredients: [],
      naturalFeed: [],
      naturalFeedAdditions: [],
    },
    operations: {
      resourceUse: {
        electricityUse: "",
        electricityUse_newValue: "",
        electricityUse_changeMetric: "",
        gasUse: "",
        gasUse_newValue: "",
        gasUse_changeMetric: "",
        waterUse: "",
        waterUse_newValue: "",
        waterUse_changeMetric: "",
        watersalinity: undefined,
        watersalinity_newValue: "",
        watersalinity_changeMetric: "",
        nitrogenConcentrationIn: "",
        nitrogenConcentrationIn_newValue: "",
        nitrogenConcentrationIn_changeMetric: "",
        nitrogenConcentrationOut: "",
        nitrogenConcentrationOut_newValue: "",
        nitrogenConcentrationOut_changeMetric: "",
        waterEnteringPondIn: "",
        waterEnteringPondIn_newValue: "",
        waterEnteringPondIn_changeMetric: "",
        waterEnteringPondOut: "",
        waterEnteringPondOut_newValue: "",
        waterEnteringPondOut_changeMetric: "",
        selfGeneratedRenewables: [],
        energyTypes: [],
        selfGeneratedRenewablesAdditions: [],
        energyTypesAdditions: [],
      },
      fertilizerTypes: [],
      materialTypes: [],
      chemicalTypes: [],
      iceAmount: "",
      iceAmount_newValue: "",
      iceAmount_changeMetric: "",
      fertilizerTypeAdditions: [],
      materialTypeAdditions: [],
      chemicalTypeAdditions: [],
    },
    output: {
      outputs: [],
      outputAdditions: [],
    },
  };
};

const mapSingleIngredientForOneStage = (
  feedBlockData: ShrimpInterventionFeed,
  feedingProgram: FeedingProgramDBModel
) => {
  const userPrefs = UserProfilePrefs.getInstance();
  const userUOM = userPrefs.getUserUnitPrefs();

  if (feedingProgram) {
    feedingProgram.singleIngredientUses?.forEach((item: SingleIngredientUseInterventionDBModel) => {
      if (item) {
        const ingredient = {
          id: item.ingredient.id || uuidv4(),
          feedType: item.ingredient.name,
          origin: item.ingredient.origin,
          kgPerAnimal: undefined,
        };

        if (item.quantity && isValidValue(item.quantity)) {
          setInterventionFloatValue(
            item,
            "quantity",
            ingredient,
            "kgPerAnimal",
            userUOM.unitBarnOutputMass,
            defaultUnitsV2.unitBarnOutputMass
          );
        }

        feedBlockData?.singleIngredients?.push(
          ingredient as InterventionFeed
        );
      }
    });

    if (
      feedingProgram.singleIngredientUsesAdditions &&
      Array.isArray(feedingProgram.singleIngredientUsesAdditions)
    ) {
      feedingProgram.singleIngredientUsesAdditions?.forEach((item: SingleIngredientUseDBModel) => {
        const newIngredient = {
          id: item.ingredient.id || uuidv4(),
          origin: item.ingredient.origin,

          feedType: item.ingredient.name,
          kgPerAnimal: floatValueInUserUnits(
            item.quantity,
            userUOM.unitBarnOutputMass,
            defaultUnitsV2.unitBarnOutputMass
          ),
        };

        feedBlockData?.singleIngredientsAdditions?.push(
          newIngredient as BaselineFeedWithOrigin
        );
      });
    }
  }
};


const mapNaturalFeedForOneStage = (
  feedBlockData: ShrimpInterventionFeed,
  feedingProgram: FeedingProgramDBModel
) => {
  const userPrefs = UserProfilePrefs.getInstance();
  const userUOM = userPrefs.getUserUnitPrefs();

  if (feedingProgram) {
    feedingProgram.naturalFeed?.forEach((item: NaturalFeedDB) => {
      if (item) {
        const naturalFeedData = {
          id: item.id || uuidv4(),
          type: item.type,
          origin: item.origin?.value,
          origin_changeMetric: item.origin?.change_type,
          quantity: undefined,
          crudeProteinContent: undefined,
        };

        if (item.quantity && isValidValue(item.quantity)) {
          setInterventionFloatValue(
            item,
            "quantity",
            naturalFeedData,
            "quantity",
            userUOM.unitBarnOutputMass,
            defaultUnitsV2.unitBarnOutputMass
          );
        }

        if (item.crudeProteinContent && isValidValue(item.crudeProteinContent)) {
          setInterventionFloatValue(
            item,
            "crudeProteinContent",
            naturalFeedData,
            "crudeProteinContent"
          );
        }

        feedBlockData?.naturalFeed?.push(
          naturalFeedData as NaturalFeedIntervention
        );
      }
    });

    if (
      feedingProgram.naturalFeedAdditions &&
      Array.isArray(feedingProgram.naturalFeedAdditions)
    ) {
      feedingProgram.naturalFeedAdditions?.forEach((item) => {
        const newFeed = {
          id: item.id || uuidv4(),
          type: item.type,
          quantity: floatValueInUserUnits(
            item.quantity,
            userUOM.unitBarnOutputMass,
            defaultUnitsV2.unitBarnOutputMass
          ),
          origin: item.origin,
          crudeProteinContent: Number(item.crudeProteinContent),
        };

        feedBlockData?.naturalFeedAdditions?.push(newFeed as NaturalFeed);
      });
    }
  }
};

export const mapFeedingProgramForOneStage = (
  formDataFeedObj: ShrimpInterventionFeed,
  stageFeedData: FeedingProgramDBModel
) => {
  mapCompoundFeedForOneStage(stageFeedData, formDataFeedObj);
  mapSingleIngredientForOneStage(formDataFeedObj, stageFeedData);
  mapNaturalFeedForOneStage(formDataFeedObj, stageFeedData);

  return formDataFeedObj;
};

const mapInputForOneStage = (
  inputBlockData: ShrimpInterventionInput,
  stageGeneralData: ShrimpStageDBModel
) => {
  const userPrefs = UserProfilePrefs.getInstance();
  const userUOM = userPrefs.getUserUnitPrefs();
  const outputMassPrecision = userPrefs.getUnitOutputMassPrecision();
  const { stageData } = stageGeneralData;
  const { input } = stageData;


  if (input) {
    if (input.totalPondArea && isValidValue(input.totalPondArea)) {
      setInterventionFloatValue(
        input,
        "totalPondArea",
        inputBlockData,
        "totalPondArea"
      );
    }

    if (input.emptyPeriod && isValidValue(input.emptyPeriod)) {
      setInterventionFloatValue(
        input,
        "emptyPeriod",
        inputBlockData,
        "emptyPeriod"
      );
    }

    // stock
    if (input.totalWeightAtStartDate && isValidValue(input.totalWeightAtStartDate)) {
      setInterventionFloatValue(
        input,
        "totalWeightAtStartDate",
        inputBlockData,
        "totalWeightAtStartDate",
        userUOM.unitOutputMass,
        defaultUnitsV2.unitOutputMass,
        outputMassPrecision
      );
    }

    if (input.totalWeightAtEndDate && isValidValue(input.totalWeightAtEndDate))
      setInterventionFloatValue(
        input,
        "totalWeightAtEndDate",
        inputBlockData,
        "totalWeightAtEndDate",
        userUOM.unitOutputMass,
        defaultUnitsV2.unitOutputMass,
        outputMassPrecision
      );

    if (input.priceAtStartDate && isValidValue(input.priceAtStartDate)) {
      setInterventionFloatValue(
        input,
        "priceAtStartDate",
        inputBlockData,
        "priceAtStartDate"
      );
    }

    if (input.priceAtEndDate && isValidValue(input.priceAtEndDate)) {
      setInterventionFloatValue(
        input,
        "priceAtEndDate",
        inputBlockData,
        "priceAtEndDate"
      );
    }

    // input - supply
    input.internalSources?.forEach((item: TempGrowingInterventionSupply) => {
      if (item) {
        const inputData = {
          id: item.id || uuidv4(),
          comingFromFarm: item.comingFromFarm,
          comingFromStage: item.comingFromStage,
          outputName: item.outputName,
          totalWeight: undefined,
          distanceTraveled: undefined,
          transportMode: item.transportMode?.value,
          transportMode_changeMetric: item.transportMode?.change_type,
        };

        if (item.totalWeight && isValidValue(item.totalWeight)) {
          setInterventionFloatValue(
            item,
            "totalWeight",
            inputData,
            "totalWeight",
            userUOM.unitOutputMass,
            defaultUnitsV2.unitOutputMass,
            outputMassPrecision
          );
        }

        if (item.distanceTraveled && isValidValue(item.distanceTraveled)) {
          setInterventionFloatValue(
            item,
            "distanceTraveled",
            inputData,
            "distanceTraveled"
          );
        }

        inputBlockData?.internalSources?.push(
          inputData as GrowingInterventionSupply
        );
      }
    });

    // input - external sources
    input.externalSources?.forEach(
      (item: TempGrowingInterventionExternalSource) => {
        if (item) {
          const externalSource = {
            id: item.id || uuidv4(),
            totalWeight: undefined,
            distanceTraveled: undefined,
            transportMode: item.transportMode?.value,
            transportMode_changeMetric: item.transportMode?.change_type,
          };

          if (item.totalWeight && isValidValue(item.totalWeight)) {
            setInterventionFloatValue(
              item,
              "totalWeight",
              externalSource,
              "totalWeight",
              userUOM.unitOutputMass,
              defaultUnitsV2.unitOutputMass,
              outputMassPrecision
            );
          }

          if (item.distanceTraveled && isValidValue(item.distanceTraveled)) {
            setInterventionFloatValue(
              item,
              "distanceTraveled",
              externalSource,
              "distanceTraveled"
            );

            inputBlockData?.externalSources?.push(
              externalSource as GrowingInterventionExternalSource
            );
          }
        }
      }
    );
  }
};

const mapOperationsForOneStage = (
  operationsBlockData: ShrimpInterventionOperations,
  stageGeneralData: ShrimpStageDBModel
) => {
  const userPrefs = UserProfilePrefs.getInstance();
  const userUOM = userPrefs.getUserUnitPrefs();
  const { stageData } = stageGeneralData;
  const { operations } = stageData;

  if (operationsBlockData && operations) {
    const { resourceUse, fertilizerTypes, materialTypes, chemicalTypes } =
      operations;

    // resourceUse
    if (resourceUse?.electricityUse && isValidValue(resourceUse?.electricityUse))
      setInterventionFloatValue(
        resourceUse,
        "electricityUse",
        operationsBlockData.resourceUse,
        "electricityUse",
        userUOM.unitElectricityUse,
        defaultUnitsV2.unitElectricityUse
      );

    if (resourceUse?.gasUse && isValidValue(resourceUse?.gasUse))
      setInterventionFloatValue(
        resourceUse,
        "gasUse",
        operationsBlockData.resourceUse,
        "gasUse",
        userUOM.unitNaturalGasUse,
        defaultUnitsV2.unitNaturalGasUse
      );

    if (resourceUse?.waterUse && isValidValue(resourceUse?.waterUse))
      setInterventionFloatValue(
        resourceUse,
        "waterUse",
        operationsBlockData.resourceUse,
        "waterUse",
        userUOM.unitWaterUse,
        defaultUnitsV2.unitWaterUse
      );

    if (
      resourceUse?.selfGeneratedRenewables &&
      Array.isArray(resourceUse.selfGeneratedRenewables)
    ) {
      resourceUse.selfGeneratedRenewables.forEach(
        (item: ShrimpEnergyTypeInputDB) => {
          if (item && item.type && item.value && isValidValue(item.value)) {
            const selfGeneratedRenewable = {
              id: uuidv4(),
              type: item.type,
              value: "",
            };

            setInterventionFloatValue(
              item,
              "value",
              selfGeneratedRenewable,
              "value",
              userUOM.unitElectricityUse,
              defaultUnitsV2.unitElectricityUse
            );

            operationsBlockData.resourceUse?.selfGeneratedRenewables?.push(
              selfGeneratedRenewable as ShrimpInterventionEnergyTypeInput
            );
          }
        }
      );
    }

    if (resourceUse?.energyTypes && Array.isArray(resourceUse?.energyTypes)) {
      resourceUse?.energyTypes.forEach((item: ShrimpEnergyTypeInputDB) => {
        if (item && item.type && item.value && isValidValue(item.value)) {
          let userUnit = "";
          let defaultUnitV2 = "";

          switch (item.type) {
            case EnergyCarrierType.Coal:
              userUnit = userUOM.unitHeatUse;
              defaultUnitV2 = defaultUnitsV2.unitHeatUse;
              break;
            case EnergyCarrierType.Diesel:
              userUnit = userUOM.unitDieselUse;
              defaultUnitV2 = defaultUnitsV2.unitDieselUse;
              break;
            case EnergyCarrierType.Gasoline:
              userUnit = userUOM.unitGasolineUse;
              defaultUnitV2 = defaultUnitsV2.unitGasolineUse;
              break;
            case EnergyCarrierType.HeavyFuelOil:
              userUnit = userUOM.unitHeavyFuelOilUse;
              defaultUnitV2 = defaultUnitsV2.unitHeavyFuelOilUse;
              break;
            case EnergyCarrierType.WoodChips:
              userUnit = userUOM.unitWoodChipsUse ?? defaultUnitsV2.unitWoodChipsUse;
              defaultUnitV2 = defaultUnitsV2.unitWoodChipsUse;
              break;
            case EnergyCarrierType.PropaneOrLpg:
              userUnit = getPropaneOrLpgUnits(userUOM, defaultUnitsV2).userUnit;
              defaultUnitV2 = getPropaneOrLpgUnits(userUOM, defaultUnitsV2).defaultUnit;
              break;
            default:
              break;
          }

          const energyType = {
            id: uuidv4(),
            type: item.type,
            value: "",
          };

          setInterventionFloatValue(
            item,
            "value",
            energyType,
            "value",
            userUnit,
            defaultUnitV2
          );

          operationsBlockData.resourceUse?.energyTypes?.push(
            energyType as ShrimpInterventionEnergyTypeInput
          );
        }
      });
    }

    if (
      resourceUse?.selfGeneratedRenewablesAdditions &&
      Array.isArray(resourceUse.selfGeneratedRenewablesAdditions)
    ) {
      resourceUse.selfGeneratedRenewablesAdditions.forEach((item) => {
        if (item && item.type) {
          const selfGeneratedRenewable = {
            id: uuidv4(),
            type: item.type,
            value: floatValueInUserUnits(
              item.value,
              userUOM.unitElectricityUse,
              defaultUnitsV2.unitElectricityUse
            ),
          };

          operationsBlockData.resourceUse?.selfGeneratedRenewablesAdditions?.push(
            selfGeneratedRenewable
          );
        }
      });
    }

    if (
      resourceUse?.energyTypesAdditions &&
      Array.isArray(resourceUse?.energyTypesAdditions)
    ) {
      resourceUse?.energyTypesAdditions.forEach((item) => {
        if (item && item.type) {
          let userUnit = "";
          let defaultUnitV2 = "";

          switch (item.type) {
            case EnergyCarrierType.Coal:
              userUnit = userUOM.unitHeatUse;
              defaultUnitV2 = defaultUnitsV2.unitHeatUse;
              break;
            case EnergyCarrierType.Diesel:
              userUnit = userUOM.unitDieselUse;
              defaultUnitV2 = defaultUnitsV2.unitDieselUse;
              break;
            case EnergyCarrierType.Gasoline:
              userUnit = userUOM.unitGasolineUse;
              defaultUnitV2 = defaultUnitsV2.unitGasolineUse;
              break;
            case EnergyCarrierType.HeavyFuelOil:
              userUnit = userUOM.unitHeavyFuelOilUse;
              defaultUnitV2 = defaultUnitsV2.unitHeavyFuelOilUse;
              break;
            case EnergyCarrierType.WoodChips:
              userUnit = userUOM.unitWoodChipsUse ?? defaultUnitsV2.unitWoodChipsUse;
              defaultUnitV2 = defaultUnitsV2.unitWoodChipsUse;
              break;
            case EnergyCarrierType.PropaneOrLpg:
              userUnit = getPropaneOrLpgUnits(userUOM, defaultUnitsV2).userUnit;
              defaultUnitV2 = getPropaneOrLpgUnits(userUOM, defaultUnitsV2).defaultUnit;
              break;
            default:
              break;
          }

          const energyType = {
            id: uuidv4(),
            type: item.type,
            value: floatValueInUserUnits(item.value, userUnit, defaultUnitV2),
          };

          operationsBlockData.resourceUse?.energyTypesAdditions?.push(
            energyType
          );
        }
      });
    }

    // pondWaterProperties
    if (resourceUse?.waterEnteringPondIn && isValidValue(resourceUse?.waterEnteringPondIn)) {
      setInterventionFloatValue(
        resourceUse,
        "waterEnteringPondIn",
        operationsBlockData.resourceUse,
        "waterEnteringPondIn",
        userUOM.unitWaterUse,
        defaultUnitsV2.unitWaterUse
      );
    }

    if (resourceUse?.nitrogenConcentrationIn && isValidValue(resourceUse?.nitrogenConcentrationIn)) {
      setInterventionFloatValue(
        resourceUse,
        "nitrogenConcentrationIn",
        operationsBlockData.resourceUse,
        "nitrogenConcentrationIn"
      );
    }

    if (resourceUse?.waterEnteringPondOut && isValidValue(resourceUse?.waterEnteringPondOut)) {
      setInterventionFloatValue(
        resourceUse,
        "waterEnteringPondOut",
        operationsBlockData.resourceUse,
        "waterEnteringPondOut",
        userUOM.unitWaterUse,
        defaultUnitsV2.unitWaterUse
      );
    }

    if (resourceUse?.nitrogenConcentrationOut && isValidValue(resourceUse?.nitrogenConcentrationOut)) {
      setInterventionFloatValue(
        resourceUse,
        "nitrogenConcentrationOut",
        operationsBlockData.resourceUse,
        "nitrogenConcentrationOut"
      );
    }

    if (resourceUse?.watersalinity && operationsBlockData.resourceUse)
      operationsBlockData.resourceUse.watersalinity = resourceUse.watersalinity;

    // ice
    if (operations.iceAmount && isValidValue(operations.iceAmount)) {
      setInterventionFloatValue(
        operations,
        "iceAmount",
        operationsBlockData,
        "iceAmount",
        userUOM.unitBarnOutputMass,
        defaultUnitsV2.unitBarnOutputMass
      );
    }

    // fertilizers
    fertilizerTypes?.forEach((item: TempFertilizerTypeInput) => {
      if (item.type && item.quantity && isValidValue(item.quantity)) {
        const fertilizerType = {
          id: item.id || uuidv4(),
          type: item.type,
          amount: undefined,
        };

        setInterventionFloatValue(
          item,
          "quantity",
          fertilizerType,
          "amount",
          userUOM.unitBarnOutputMass,
          defaultUnitsV2.unitBarnOutputMass
        );

        operationsBlockData.fertilizerTypes?.push(
          fertilizerType as FertilizerTypeInput
        );
      }
    });

    if (
      operations.fertilizerTypeAdditions &&
      Array.isArray(operations.fertilizerTypeAdditions)
    ) {
      operations.fertilizerTypeAdditions?.forEach((item) => {
        if (item.type) {
          const newFertilizer = {
            id: item.id || uuidv4(),
            amount: floatValueInUserUnits(
              item.quantity,
              userUOM.unitBarnOutputMass,
              defaultUnitsV2.unitBarnOutputMass
            ),
            type: item.type,
          };

          operationsBlockData?.fertilizerTypeAdditions?.push(
            newFertilizer as ShrimpBaselineFertilizerTypeInput
          );
        }
      });
    }

    // materialsUse
    materialTypes?.forEach((item: ShrimpGrowingStageMaterialTypeInput) => {
      if (item && item.type) {
        const materialType = {
          id: item.id || uuidv4(),
          type: item.type,
          amount: undefined,
          lifetimeOfMaterial: undefined,
          transportMode: item.transportMode?.value,
          transportMode_changeMetric: item.transportMode?.change_type,
          distanceTraveled: undefined,
        };

        if (item.lifetimeOfMaterial && isValidValue(item.lifetimeOfMaterial)) {
          setInterventionFloatValue(
            item,
            "lifetimeOfMaterial",
            materialType,
            "lifetimeOfMaterial"
          );
        }

        if (item.amount && isValidValue(item.amount)) {
          setInterventionFloatValue(
            item,
            "amount",
            materialType,
            "amount",
            userUOM.unitBarnOutputMass,
            defaultUnitsV2.unitBarnOutputMass
          );
        }

        if (item.distanceTraveled && isValidValue(item.distanceTraveled)) {
          setInterventionFloatValue(
            item,
            "distanceTraveled",
            materialType,
            "distanceTraveled",
            getUserTransportUnit(item.transportMode),
            getDefaultTransportUnit(item.transportMode)
          );
        }

        operationsBlockData.materialTypes?.push(
          materialType as ShrimpMaterialTypeInput
        );
      }
    });

    if (
      operations.materialTypeAdditions &&
      Array.isArray(operations.materialTypeAdditions)
    ) {
      operations.materialTypeAdditions?.forEach(
        (item: StageMaterialDBModel) => {
          if (item && item.type) {
            let transportModeValue;

            if (item.transport) {
              transportModeValue = item.transport[0].mode;
            }

            const newMaterial = {
              id: item.id || uuidv4(),
              type: item.type,
              amount: floatValueInUserUnits(
                item.amount,
                userUOM.unitBarnOutputMass,
                defaultUnitsV2.unitBarnOutputMass
              ),
              lifetimeOfMaterial: floatValueInUserUnits(item.lifeTime),
              transportMode: transportModeValue,
              distanceTraveled: item.transport
                ? floatValueInUserUnits(
                  item.transport[0]?.distance,
                  getUserTransportUnit(item.transport[0]?.mode),
                  getDefaultTransportUnit(item.transport[0]?.mode)
                )
                : 0,
            };

            operationsBlockData.materialTypeAdditions?.push(
              newMaterial as ShrimpBaselineMaterialTypeInput
            );
          }
        }
      );
    }

    // chemicalsUse
    chemicalTypes?.forEach((item: ShrimpChemicalTypeInput) => {
      if (item && item.type && item.amount && isValidValue(item.amount)) {
        const chemicalType = {
          id: item.id || uuidv4(),
          type: item.type,
          amount: undefined,
        };

        setInterventionFloatValue(
          item,
          "amount",
          chemicalType,
          "amount",
          userUOM.unitBarnOutputMass,
          defaultUnitsV2.unitBarnOutputMass
        );

        operationsBlockData.chemicalTypes?.push(
          chemicalType as ShrimpInterventionChemicalTypeInput
        );
      }
    });

    if (
      operations.chemicalTypeAdditions &&
      Array.isArray(operations.chemicalTypeAdditions)
    ) {
      operations.chemicalTypeAdditions?.forEach((item) => {
        if (item && item.type) {
          const newChemical = {
            id: item.id || uuidv4(),
            amount: floatValueInUserUnits(
              item.amount,
              userUOM.unitBarnOutputMass,
              defaultUnitsV2.unitBarnOutputMass
            ),
            type: item.type,
          };

          operationsBlockData.chemicalTypeAdditions?.push(
            newChemical as ShrimpBaselineChemicalTypeInput
          );
        }
      });
    }
  }
};

const mapOutputForOneStage = (
  outputBlockData: ShrimpInterventionOutputs,
  stageGeneralData: ShrimpStageDataDBModel
) => {
  const userPrefs = UserProfilePrefs.getInstance();
  const userUOM = userPrefs.getUserUnitPrefs();
  const { output } = stageGeneralData;

  if (output) {
    output.outputs?.forEach((item: ShrimpOutput) => {
      if (item && item.name) {
        const outputData = {
          id: item.id || uuidv4(),
          calculateLca: item.calculateLca,
          name: item.name,
          price: undefined,
          soldExternally: item.soldExternally,
          totalWeight: undefined,
          proteinContent: undefined,
        };

        if (item.price && isValidValue(item.price)) {
          setInterventionFloatValue(
            item,
            "price",
            outputData,
            "price"
          );
        }

        if (item.totalWeight && isValidValue(item.totalWeight)) {
          setInterventionFloatValue(
            item,
            "totalWeight",
            outputData,
            "totalWeight",
            userUOM.unitBarnOutputMass,
            defaultUnitsV2.unitBarnOutputMass
          );
        }

        if (item.proteinContent && isValidValue(item.proteinContent)) {
          setInterventionFloatValue(
            item,
            "proteinContent",
            outputData,
            "proteinContent"
          );
        }

        outputBlockData?.outputs?.push(outputData as ShrimpInterventionOutput);
      }
    });

    if (output.outputAdditions && Array.isArray(output.outputAdditions)) {
      output.outputAdditions?.forEach((item) => {
        const newOutput = {
          id: item.id || uuidv4(),
          calculateLca: item.calculateLca,
          name: item.name,
          price: Number(item.price),
          soldExternally: item.soldExternally ? "Yes" : "No",
          totalWeight: floatValueInUserUnits(
            item.weight,
            userUOM.unitBarnOutputMass,
            defaultUnitsV2.unitBarnOutputMass
          ),
          proteinContent: Number(item.proteinContent),
        };

        outputBlockData.outputAdditions?.push(
          newOutput as ShrimpBaselineOutput
        );
      });
    }
  }
};

const mapStageInterventionDataInSustell = (
  stageBlockData: ShrimpStageDBModel
) => {
  const { stageData } = stageBlockData;

  const oneStageData = {
    id: stageBlockData?.id || uuidv4(),
    name: stageBlockData?.name,
    type: stageBlockData?.type,
    stageData: { ...initStageData() },
  };

  if (stageData.input && oneStageData.stageData.input) {
    mapInputForOneStage(oneStageData.stageData.input, stageBlockData);
  }

  if (stageData.feedingProgram && oneStageData.stageData.feed)
    mapFeedingProgramForOneStage(
      oneStageData.stageData.feed,
      stageData.feedingProgram
    );

  if (stageData.operations && oneStageData.stageData.operations) {
    mapOperationsForOneStage(oneStageData.stageData.operations, stageBlockData);
  }

  if (stageData.output && oneStageData.stageData.output) {
    mapOutputForOneStage(oneStageData.stageData.output, stageData);
  }

  return oneStageData;
};

const mapShrimpInterventionDataInSustell = (
  inDataObj: ShrimpInterventionDBModel
) => {
  if (inDataObj) {
    let dataObj: ShrimpInterventionDBModel = { ...inDataObj, stages: [] };
    inDataObj.stages?.forEach((stage: ShrimpStageDBModel) => {
      let stageNew: ShrimpStageDBModel;
      if (stage.stageData && typeof stage.stageData === "string") {
        stageNew = { ...stage, stageData: JSON.parse(stage.stageData) || {} };
      } else {
        stageNew = { ...stage };
      }
      dataObj.stages?.push(stageNew);
    });

    const payloadObj = createCoreForm(dataObj);

    if (dataObj.resourceUse)
      mapResourceUse(dataObj.resourceUse, payloadObj.resourceUse);

    if (dataObj.stages && Array.isArray(dataObj.stages)) {
      return dataObj.stages.reduce(
        (payload, stage) => {
          const mappedStage = mapStageInterventionDataInSustell(stage);

          return {
            ...payload,
            stages: [...payload.stages, mappedStage],
          };
        },
        { ...payloadObj }
      );
    }

    return payloadObj;
  }

  return {};
};

export default mapShrimpInterventionDataInSustell;
